import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'

import { Flex, Spacing, TabsComponent } from '@enechain/ecloud-designsystem'
import { FaWrenchRegular } from '@enechain/ecloud-icons'

import { EmptyPanel } from '~/components/common/EmptyPanel'
import { PageBody } from '~/components/common/PageBody'
import { PageIntro } from '~/components/common/PageIntro'
import { PageLayout } from '~/components/common/PageLayout'
import { PageScrollArea } from '~/components/common/PageScrollArea'
import { tabTriggerFontSize } from '~/constants/tabStyle'
import { ContractTab } from '~/pages/CustomerInfo/components/ContractTab'
import { ContractorTab } from '~/pages/CustomerInfo/components/ContractorTab'
import { CustomerListTab } from '~/pages/CustomerInfo/components/CustomerListTab'
import { routePaths } from '~/routes/routePaths'

type TabKey = 'list' | 'contractors' | 'contract' | 'schedule'

/** タブの並びはモック (`Customer.razor` の `_topTabs`) と同じ: 需要家一覧 / 契約者 / 契約推移。 */
const TABS: ReadonlyArray<{ key: TabKey; path: string; labelKey: string }> = [
  {
    key: 'list',
    path: routePaths.customerList.path,
    labelKey: 'page:customerInfo.tabs.list',
  },
  {
    key: 'contractors',
    path: routePaths.customerContractors.path,
    labelKey: 'page:customerInfo.tabs.contractors',
  },
  {
    key: 'contract',
    path: routePaths.customerContract.path,
    labelKey: 'page:customerInfo.tabs.contract',
  },
  {
    key: 'schedule',
    path: routePaths.customerSchedule.path,
    labelKey: 'page:customerInfo.tabs.schedule',
  },
]

/**
 * 需要家管理 (需要家一覧 / 契約者 / 契約推移 / 需要特性)。 3 つの route が同じ page を出し、
 * タブで route を切り替える (初期表示は需要家一覧)。 需要特性タブは別の Work Item で足す。
 */
export const CustomerInfoPage = (): React.ReactElement => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const activeTab: TabKey =
    TABS.find((tab) => pathname.startsWith(tab.path))?.key ?? 'list'

  return (
    <PageLayout
      breadcrumbItems={[
        { label: t('page:customerInfo.breadcrumb.group') },
        { label: t('page:customerInfo.breadcrumb.page'), bold: true },
      ]}
    >
      <PageBody>
        <PageScrollArea>
          <Flex flexDirection="column" gap={Spacing[4].value}>
            {/* #9851 で需要特性の文言を戻す (page:customerInfo.descriptionWithDemandProfile) */}
            <PageIntro description={t('page:customerInfo.description')} />
            <TabsComponent
              items={TABS.map((tab) => ({
                key: tab.key,
                label: t(tab.labelKey),
              }))}
              onSelect={(key) => {
                const next = TABS.find((tab) => tab.key === key)
                if (next !== undefined) void navigate(next.path)
              }}
              selected={activeTab}
              tabSx={{ fontSize: tabTriggerFontSize.raw }}
            />
            {activeTab === 'list' && <CustomerListTab />}
            {activeTab === 'contractors' && <ContractorTab />}
            {activeTab === 'contract' && <ContractTab />}
            {/* 中身は #9851 で作る。 それまでは準備中の空状態 */}
            {activeTab === 'schedule' && (
              <EmptyPanel
                description={t('page:customerInfo.schedule.description')}
                icon={<FaWrenchRegular />}
                title={t('page:customerInfo.schedule.title')}
              />
            )}
          </Flex>
        </PageScrollArea>
      </PageBody>
    </PageLayout>
  )
}
